/**
 * Which hosts HostGuard redirects a non-admin visitor away from.
 *
 * Only Lovable's preview surface. Everything else is left alone: localhost, the
 * Lovable sandbox dev host (*.lovable.dev) and every deploy of this repo
 * (tms.aurora.tech and its dev/staging/pr-<N> subdomains) are all builds we
 * control, and a host that is neither ours nor Lovable's isn't something our
 * non-admin users should be moved around for.
 *
 * Why only Lovable's previews: those builds read the committed `.env`, which
 * points at Lovable's *production* Supabase project, so a shared preview link is
 * unpublished UI operating on real data. See `docs/plans/PREVIEW_BRANCH_AUTH_SHIM.md`.
 */

/** The published app, which no visitor is ever redirected away from. */
export const PROD_ORIGIN = "https://aurora-tms.lovable.app";

const PUBLISHED_HOST = new URL(PROD_ORIGIN).hostname;

/**
 * Lovable's zones, as the app itself lists them in
 * `src/integrations/supabase/previewAuthStorage.ts`: the editor's preview
 * iframe and shareable preview tab live on subdomains of these, and the `-dev`
 * zone is their staging counterpart. The published app is one host in the first
 * zone and is excluded below.
 */
const LOVABLE_PREVIEW_ZONES: readonly string[] = [
  "lovable.app",
  "lovableproject.com",
  "lovableproject-dev.com",
];

/**
 * A preview host: `<something>.lovable.app` (or one of the other Lovable
 * zones), excluding the published app. Suffix matching with the leading dot, so
 * `tms.aurora.tech` and a host like `evil-lovable.app` don't qualify.
 */
export function isLovablePreviewHost(hostname: string): boolean {
  if (hostname === PUBLISHED_HOST) return false;
  return LOVABLE_PREVIEW_ZONES.some((zone) => hostname.endsWith(`.${zone}`));
}

/**
 * Whether HostGuard should send this visitor to the published app. Client-side
 * and a courtesy, not a security boundary: RLS still governs data access.
 */
export function shouldGuardHost(hostname: string): boolean {
  return isLovablePreviewHost(hostname);
}
